import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  List,
  Star,
  ChevronDown as ChevronDownIcon,
  Edit,
  Trash2,
  MoreVertical,
  LayoutDashboard,
  Kanban,
  MessageSquare,
  Paperclip,
  Vote as VoteIcon,
  FileText,
  Users2,
  Video,
  Users
} from 'lucide-react';
import ProjectNavigationTabs, { TabConfig } from './ProjectNavigationTabs';

interface ProjectData {
  _id: string;
  title: string;
  color: string;
  creator: { _id: string; name: string; email: string };
}

interface ProjectNavbarProps {
  project: ProjectData | null;
  projectId: string;
  isCreator?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
}

const ProjectNavbar: React.FC<ProjectNavbarProps> = ({
  project,
  projectId,
  isCreator = false,
  onEdit,
  onDelete,
  className = ''
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  if (!project) return null;

  const getTabs = (): TabConfig[] => {
    const basePath = `/projects/${projectId}`;
    const isBasePath = location.pathname === basePath;
    
    const tabs: TabConfig[] = [
      {
        id: 'overview',
        label: 'Overview',
        icon: LayoutDashboard,
        onClick: () => navigate(basePath),
        active: isBasePath && !location.search.includes('tab=')
      },
      {
        id: 'tasks',
        label: 'Tasks',
        icon: Kanban,
        onClick: () => navigate(`${basePath}?tab=tasks`),
        active:
          location.search.includes('tab=tasks') ||
          location.search.includes('tab=list') ||
          location.search.includes('tab=board') ||
          location.search.includes('tab=timeline') ||
          location.search.includes('subtab=tasks') ||
          location.pathname.includes('/tasks') ||
          location.pathname.includes('/timeline')
      },
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        onClick: () => navigate(`${basePath}?tab=dashboard`),
        active: location.search.includes('tab=dashboard')
      },
      
      {
        id: 'messages',
        label: 'Messages',
        icon: MessageSquare,
        onClick: () => navigate(`${basePath}?tab=messages`),
        active: location.search.includes('tab=messages')
      },
      {
        id: 'notes',
        label: 'Notes',
        icon: FileText,
        onClick: () => navigate(`${basePath}?tab=notes`),
        active: location.search.includes('tab=notes')
      },
      {
        id: 'collaboration',
        label: 'Collaboration',
        icon: Users2,
        onClick: () => navigate(`${basePath}?tab=collaboration`),
        active: location.search.includes('tab=collaboration') && !location.search.includes('subtab=tasks')
      },
      {
        id: 'meetings',
        label: 'Meeting calendar',
        icon: Video,
        onClick: () => navigate(`${basePath}?tab=meetings`),
        active: location.search.includes('tab=meetings')
      }
    ];

    // Ajouter l'onglet "Équipe" uniquement pour le créateur
    if (isCreator) {
      tabs.push({
        id: 'equipe',
        label: 'Team',
        icon: Users,
        onClick: () => navigate(`${basePath}?tab=equipe`),
        active: location.search.includes('tab=equipe')
      });
    }

    return tabs;
  };

  return (
    <div className={`bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm ${className}`}>
      <div className="max-w-full mx-auto px-4 py-2.5">
        {/* Project Header - Compact */}
        <div className="flex items-center gap-2.5 mb-2">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
            style={{ backgroundColor: project.color }}
          >
            <List className="h-4 w-4" />
          </div>
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 truncate">{project.title}</h1>
            <button 
              onClick={() => navigate(`/projects/${project._id}`)}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
            >
              <ChevronDownIcon className="h-4 w-4" />
            </button>
            <button className="text-gray-400 hover:text-yellow-500 flex-shrink-0">
              <Star className="h-4 w-4" />
            </button>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700 flex-shrink-0">
              <div className="w-1 h-1 rounded-full bg-green-500"></div>
              On track
            </span>
          </div>
          {isCreator && (
            <div className="flex items-center gap-1 flex-shrink-0">
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="Modifier"
                >
                  <Edit className="h-4 w-4" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={onDelete}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors">
                <MoreVertical className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Navigation Tabs */}
        <ProjectNavigationTabs tabs={getTabs()} />
      </div>
    </div>
  );
};

export default ProjectNavbar;

